
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Popover,
  Typography,
  Divider,
} from '@mui/material';
import { Autocomplete, Popper } from '@mui/material';
import {
  FlightTakeoff as DepartIcon,
  FlightLand as ArriveIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  SwapHoriz as SwapIcon,
  Add,
  Remove,
  ChildCare,
  ChildFriendly,
  AirlineSeatReclineExtra,
} from '@mui/icons-material';

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 10,
  label,
  sublabel,
  icon,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        size="small"
        onClick={() => value > min && onChange(value - 1)}
        disabled={value <= min}
        sx={{
          border: '1px solid',
          borderColor: value <= min ? '#e0e0e0' : '#2D6E6A',
          color: value <= min ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Remove fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
        {value}
      </Typography>
      <IconButton
        size="small"
        onClick={() => value < max && onChange(value + 1)}
        disabled={value >= max}
        sx={{
          border: '1px solid',
          borderColor: value >= max ? '#e0e0e0' : '#2D6E6A',
          color: value >= max ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

// ============================================================================
// TYPES
// ============================================================================

interface FlightSearchFormProps {
  onSearch?: (params: FlightSearchParams) => void;
}

interface FlightSearchParams {
  tripType: 'roundtrip' | 'oneway' | 'multicity';
  origin: string;
  destination: string;
  departDate: string;
  returnDate: string;
  adults: number;
  children: number;
  infants: number;
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first';
}
// ============================================================================
// AIRPORT DATA & AUTOCOMPLETE
// ============================================================================

interface AirportOption {
  code: string;
  name: string;
  city: string;
  country: string;
  label: string;
}

const AIRPORTS: AirportOption[] = [
  // United States - Major Hubs
  { code: 'JFK', name: 'John F. Kennedy International', city: 'New York', country: 'US', label: 'JFK - New York John F. Kennedy' },
  { code: 'LGA', name: 'LaGuardia', city: 'New York', country: 'US', label: 'LGA - New York LaGuardia' },
  { code: 'EWR', name: 'Newark Liberty International', city: 'Newark', country: 'US', label: 'EWR - Newark Liberty International' },
  { code: 'LAX', name: 'Los Angeles International', city: 'Los Angeles', country: 'US', label: 'LAX - Los Angeles International' },
  { code: 'SFO', name: 'San Francisco International', city: 'San Francisco', country: 'US', label: 'SFO - San Francisco International' },
  { code: 'ORD', name: "O'Hare International", city: 'Chicago', country: 'US', label: "ORD - Chicago O'Hare" },
  { code: 'MDW', name: 'Midway International', city: 'Chicago', country: 'US', label: 'MDW - Chicago Midway' },
  { code: 'ATL', name: 'Hartsfield-Jackson Atlanta International', city: 'Atlanta', country: 'US', label: 'ATL - Atlanta Hartsfield-Jackson' },
  { code: 'DFW', name: 'Dallas/Fort Worth International', city: 'Dallas', country: 'US', label: 'DFW - Dallas/Fort Worth' },
  { code: 'DEN', name: 'Denver International', city: 'Denver', country: 'US', label: 'DEN - Denver International' },
  { code: 'SEA', name: 'Seattle-Tacoma International', city: 'Seattle', country: 'US', label: 'SEA - Seattle-Tacoma' },
  { code: 'MIA', name: 'Miami International', city: 'Miami', country: 'US', label: 'MIA - Miami International' },
  { code: 'FLL', name: 'Fort Lauderdale-Hollywood International', city: 'Fort Lauderdale', country: 'US', label: 'FLL - Fort Lauderdale' },
  { code: 'MCO', name: 'Orlando International', city: 'Orlando', country: 'US', label: 'MCO - Orlando International' },
  { code: 'BOS', name: 'Logan International', city: 'Boston', country: 'US', label: 'BOS - Boston Logan' },
  { code: 'PHX', name: 'Phoenix Sky Harbor International', city: 'Phoenix', country: 'US', label: 'PHX - Phoenix Sky Harbor' },
  { code: 'IAH', name: 'George Bush Intercontinental', city: 'Houston', country: 'US', label: 'IAH - Houston George Bush' },
  { code: 'HOU', name: 'William P. Hobby', city: 'Houston', country: 'US', label: 'HOU - Houston Hobby' },
  { code: 'LAS', name: 'Harry Reid International', city: 'Las Vegas', country: 'US', label: 'LAS - Las Vegas Harry Reid' },
  { code: 'MSP', name: 'Minneapolis-Saint Paul International', city: 'Minneapolis', country: 'US', label: 'MSP - Minneapolis-Saint Paul' },
  { code: 'DTW', name: 'Detroit Metropolitan', city: 'Detroit', country: 'US', label: 'DTW - Detroit Metropolitan' },
  { code: 'PHL', name: 'Philadelphia International', city: 'Philadelphia', country: 'US', label: 'PHL - Philadelphia International' },
  { code: 'CLT', name: 'Charlotte Douglas International', city: 'Charlotte', country: 'US', label: 'CLT - Charlotte Douglas' },
  { code: 'SAN', name: 'San Diego International', city: 'San Diego', country: 'US', label: 'SAN - San Diego International' },
  { code: 'TPA', name: 'Tampa International', city: 'Tampa', country: 'US', label: 'TPA - Tampa International' },
  { code: 'DCA', name: 'Ronald Reagan Washington National', city: 'Washington', country: 'US', label: 'DCA - Washington Reagan National' },
  { code: 'IAD', name: 'Washington Dulles International', city: 'Washington', country: 'US', label: 'IAD - Washington Dulles' },
  { code: 'BWI', name: 'Baltimore/Washington International', city: 'Baltimore', country: 'US', label: 'BWI - Baltimore/Washington' },
  // International - Europe
  { code: 'LHR', name: 'Heathrow', city: 'London', country: 'UK', label: 'LHR - London Heathrow' },
  { code: 'LGW', name: 'Gatwick', city: 'London', country: 'UK', label: 'LGW - London Gatwick' },
  { code: 'STN', name: 'Stansted', city: 'London', country: 'UK', label: 'STN - London Stansted' },
  { code: 'CDG', name: 'Charles de Gaulle', city: 'Paris', country: 'FR', label: 'CDG - Paris Charles de Gaulle' },
  { code: 'ORY', name: 'Orly', city: 'Paris', country: 'FR', label: 'ORY - Paris Orly' },
  { code: 'AMS', name: 'Schiphol', city: 'Amsterdam', country: 'NL', label: 'AMS - Amsterdam Schiphol' },
  { code: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'DE', label: 'FRA - Frankfurt Airport' },
  { code: 'MUC', name: 'Munich Airport', city: 'Munich', country: 'DE', label: 'MUC - Munich Airport' },
  { code: 'BCN', name: 'El Prat', city: 'Barcelona', country: 'ES', label: 'BCN - Barcelona El Prat' },
  { code: 'MAD', name: 'Adolfo Suárez Madrid-Barajas', city: 'Madrid', country: 'ES', label: 'MAD - Madrid Barajas' },
  { code: 'FCO', name: 'Leonardo da Vinci-Fiumicino', city: 'Rome', country: 'IT', label: 'FCO - Rome Fiumicino' },
  { code: 'MXP', name: 'Malpensa', city: 'Milan', country: 'IT', label: 'MXP - Milan Malpensa' },
  // International - Asia & Pacific
  { code: 'NRT', name: 'Narita International', city: 'Tokyo', country: 'JP', label: 'NRT - Tokyo Narita' },
  { code: 'HND', name: 'Haneda', city: 'Tokyo', country: 'JP', label: 'HND - Tokyo Haneda' },
  { code: 'ICN', name: 'Incheon International', city: 'Seoul', country: 'KR', label: 'ICN - Seoul Incheon' },
  { code: 'HKG', name: 'Hong Kong International', city: 'Hong Kong', country: 'HK', label: 'HKG - Hong Kong International' },
  { code: 'SIN', name: 'Changi', city: 'Singapore', country: 'SG', label: 'SIN - Singapore Changi' },
  { code: 'BKK', name: 'Suvarnabhumi', city: 'Bangkok', country: 'TH', label: 'BKK - Bangkok Suvarnabhumi' },
  { code: 'SYD', name: 'Kingsford Smith', city: 'Sydney', country: 'AU', label: 'SYD - Sydney Kingsford Smith' },
  { code: 'DXB', name: 'Dubai International', city: 'Dubai', country: 'AE', label: 'DXB - Dubai International' },
  // Americas
  { code: 'YYZ', name: 'Toronto Pearson International', city: 'Toronto', country: 'CA', label: 'YYZ - Toronto Pearson' },
  { code: 'YVR', name: 'Vancouver International', city: 'Vancouver', country: 'CA', label: 'YVR - Vancouver International' },
  { code: 'MEX', name: 'Benito Juárez International', city: 'Mexico City', country: 'MX', label: 'MEX - Mexico City' },
  { code: 'CUN', name: 'Cancún International', city: 'Cancún', country: 'MX', label: 'CUN - Cancún International' },
  { code: 'GRU', name: 'São Paulo-Guarulhos', city: 'São Paulo', country: 'BR', label: 'GRU - São Paulo Guarulhos' },
  // Caribbean
  { code: 'SJU', name: 'Luis Muñoz Marín International', city: 'San Juan', country: 'PR', label: 'SJU - San Juan Puerto Rico' },
  { code: 'MBJ', name: 'Sangster International', city: 'Montego Bay', country: 'JM', label: 'MBJ - Montego Bay Jamaica' },
  { code: 'NAS', name: 'Lynden Pindling International', city: 'Nassau', country: 'BS', label: 'NAS - Nassau Bahamas' },
  { code: 'PUJ', name: 'Punta Cana International', city: 'Punta Cana', country: 'DO', label: 'PUJ - Punta Cana' },
];

const AREA_TO_AIRPORTS: Record<string, string[]> = {
  'manhattan': ['JFK', 'LGA', 'EWR'],
  'new york': ['JFK', 'LGA', 'EWR'],
  'nyc': ['JFK', 'LGA', 'EWR'],
  'brooklyn': ['JFK', 'LGA', 'EWR'],
  'south beach': ['MIA', 'FLL'],
  'miami beach': ['MIA', 'FLL'],
  'downtown miami': ['MIA'],
  'hollywood': ['LAX', 'BUR'],
  'beverly hills': ['LAX', 'BUR'],
  'silicon valley': ['SFO', 'SJC'],
  'downtown chicago': ['ORD', 'MDW'],
  'loop': ['ORD', 'MDW'],
  'vegas': ['LAS'],
  'strip': ['LAS'],
  'times square': ['JFK', 'LGA', 'EWR'],
  'central london': ['LHR', 'LGW', 'STN'],
};

const filterAirports = (query: string): AirportOption[] => {
  if (!query || query.length < 2) return [];
  const normalizedQuery = query.toLowerCase().trim();

  const areaMatch = Object.entries(AREA_TO_AIRPORTS).find(([area]) =>
      normalizedQuery.includes(area) || area.includes(normalizedQuery)
  );

  if (areaMatch) {
      const [, airportCodes] = areaMatch;
      return AIRPORTS.filter(a => airportCodes.includes(a.code));
  }

  return AIRPORTS.filter(airport => {
      const searchFields = [
          airport.code.toLowerCase(),
          airport.city.toLowerCase(),
          airport.name.toLowerCase(),
      ];
      return searchFields.some(field => field.includes(normalizedQuery));
  }).slice(0, 8);
};

const AutocompletePopper = (props: any) => (
  <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
);
// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FlightSearchForm: React.FC<FlightSearchFormProps> = ({ onSearch }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const [formData, setFormData] = useState<FlightSearchParams>({
    tripType: 'roundtrip',
    origin: '',
    destination: '',
    departDate: today,
    returnDate: nextWeek,
    adults: 1,
    children: 0,
    infants: 0,
    cabinClass: 'economy',
  });

  // Traveler selector popover
  const [travelerAnchor, setTravelerAnchor] = useState<HTMLElement | null>(null);
  const [dateError, setDateError] = useState('');

  const handleChange = (field: keyof FlightSearchParams) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDateError('');
    const newData = { ...formData, [field]: e.target.value };

    if (field === 'departDate' && formData.tripType === 'roundtrip' && e.target.value > newData.returnDate) {
      const nextDay = new Date(e.target.value);
      nextDay.setDate(nextDay.getDate() + 7);
      newData.returnDate = nextDay.toISOString().split('T')[0];
    }

    if (field === 'returnDate' && e.target.value < newData.departDate) {
      setDateError('Return date must be on or after departure date');
      return;
    }

    setFormData(newData);
  };

  const handleSwapLocations = () => {
    setFormData({
      ...formData,
      origin: formData.destination,
      destination: formData.origin,
    });
  };

  const handleSearch = () => {
    if (!formData.origin.trim() || !formData.destination.trim()) {
      setDateError('Please enter origin and destination');
      return;
    }
    if (formData.tripType === 'roundtrip' && formData.returnDate < formData.departDate) {
      setDateError('Return date must be on or after departure date');
      return;
    }
    setDateError('');
    if (onSearch) {
      onSearch(formData);
    } else {
      const params = new URLSearchParams({
        tripType: formData.tripType,
        origin: formData.origin,
        destination: formData.destination,
        departDate: formData.departDate,
        ...(formData.tripType === 'roundtrip' && { returnDate: formData.returnDate }),
        adults: formData.adults.toString(),
        children: formData.children.toString(),
        infants: formData.infants.toString(),
        cabinClass: formData.cabinClass,
      });
      navigate(`/search/flights?${params.toString()}`);
    }
  };

  // Cabin class options
  const cabinClasses = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First Class' },
  ];

  // Traveler summary text
  const totalTravelers = formData.adults + formData.children + formData.infants;
  const travelerSummary = `${totalTravelers} traveler${totalTravelers > 1 ? 's' : ''}, ${cabinClasses.find(c => c.value === formData.cabinClass)?.label || 'Economy'}`;

  // Return date min (same as or after depart date)
  const returnDateMin = formData.departDate || today;

  return (
    <Box>
      {dateError && (
        <Alert severity="error" onClose={() => setDateError('')} sx={{ mb: 1.5, borderRadius: 2 }}>
          {dateError}
        </Alert>
      )}
      {/* Trip Type */}
      <RadioGroup
        row
        value={formData.tripType}
        onChange={(e) => setFormData({ ...formData, tripType: e.target.value as FlightSearchParams['tripType'] })}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value="roundtrip"
          control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} />}
          label="Round-trip"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
        />
        <FormControlLabel
          value="oneway"
          control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} />}
          label="One-way"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
        />
        <FormControlLabel
          value="multicity"
          control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} />}
          label="Multi-city"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
        />
      </RadioGroup>

      <Grid container spacing={2} alignItems="flex-end">
        {/* Origin */}
        <Grid item xs={12} md={2.5}>
          <Autocomplete
            freeSolo
            options={filterAirports(formData.origin)}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={formData.origin}
            onInputChange={(_, newValue) => setFormData({ ...formData, origin: newValue })}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                setFormData({ ...formData, origin: newValue.code + ' - ' + newValue.city });
              }
            }}
            PopperComponent={AutocompletePopper}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    bgcolor: '#E3F2FD',
                    color: '#1976D2',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    minWidth: 45,
                    textAlign: 'center',
                  }}>
                    {option.code}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.city}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="From"
                placeholder="City or airport"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <DepartIcon color="primary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
        </Grid>

        {/* Swap Button */}
        <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <IconButton
            onClick={handleSwapLocations}
            sx={{
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            <SwapIcon />
          </IconButton>
        </Grid>

        {/* Destination */}
        <Grid item xs={12} md={2.5}>
          <Autocomplete
            freeSolo
            options={filterAirports(formData.destination)}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={formData.destination}
            onInputChange={(_, newValue) => setFormData({ ...formData, destination: newValue })}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                setFormData({ ...formData, destination: newValue.code + ' - ' + newValue.city });
              }
            }}
            PopperComponent={AutocompletePopper}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{
                    bgcolor: '#E8F5E9',
                    color: '#2E7D32',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    minWidth: 45,
                    textAlign: 'center',
                  }}>
                    {option.code}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.city}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="To"
                placeholder="City or airport"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <ArriveIcon color="primary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
        </Grid>

        {/* Depart Date */}
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            label="Depart"
            type="date"
            value={formData.departDate}
            onChange={handleChange('departDate')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Return Date */}
        {formData.tripType === 'roundtrip' && (
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="Return"
              type="date"
              value={formData.returnDate}
              onChange={handleChange('returnDate')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: returnDateMin }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        )}

        {/* Travelers & Class */}
        <Grid item xs={12} md={formData.tripType === 'roundtrip' ? 2.5 : 3}>
          <TextField
            fullWidth
            label="Travelers & Class"
            value={travelerSummary}
            onClick={(e) => setTravelerAnchor(e.currentTarget)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                cursor: 'pointer',
              },
              '& .MuiOutlinedInput-input': {
                cursor: 'pointer',
              },
            }}
          />

          {/* Traveler Selector Popover */}
          <Popover
            open={Boolean(travelerAnchor)}
            anchorEl={travelerAnchor}
            onClose={() => setTravelerAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                p: 2,
                width: 320,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Passengers
            </Typography>

            <StepperInput
              value={formData.adults}
              onChange={(val) => setFormData({ ...formData, adults: val })}
              min={1}
              max={9}
              label="Adults"
              sublabel="Age 12+"
              icon={<PersonIcon sx={{ color: '#666' }} />}
            />
            <Divider sx={{ my: 1 }} />
            <StepperInput
              value={formData.children}
              onChange={(val) => setFormData({ ...formData, children: val })}
              min={0}
              max={8}
              label="Children"
              sublabel="Age 2-11"
              icon={<ChildCare sx={{ color: '#666' }} />}
            />
            <Divider sx={{ my: 1 }} />
            <StepperInput
              value={formData.infants}
              onChange={(val) => setFormData({ ...formData, infants: val })}
              min={0}
              max={formData.adults}
              label="Infants"
              sublabel="Under 2, on lap"
              icon={<ChildFriendly sx={{ color: '#666' }} />}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AirlineSeatReclineExtra sx={{ fontSize: 18 }} /> Cabin Class
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={formData.cabinClass}
              onChange={(e) => setFormData({ ...formData, cabinClass: e.target.value as FlightSearchParams['cabinClass'] })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {cabinClasses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Infant warning */}
            {formData.infants > formData.adults && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                Each infant must be accompanied by an adult
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={() => setTravelerAnchor(null)}
              sx={{
                mt: 2,
                bgcolor: '#2D6E6A',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#009688' },
              }}
            >
              Done
            </Button>
          </Popover>
        </Grid>

        {/* Search Button */}
        <Grid item xs={12} md={formData.tripType === 'roundtrip' ? 1.5 : 2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!formData.origin || !formData.destination}
            startIcon={<SearchIcon />}
            sx={{
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FlightSearchForm;
